import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { api, photoUrl, audioUrl, exportDownloadUrl } from "../lib/api";
import { isEstimatedDate } from "./PersonCard";
import { toast } from "sonner";
import { Play, Pause, SkipForward, SkipBack, RotateCcw, Loader2, User, Clapperboard, Wand2, Save, Download, Volume2, Music2 } from "lucide-react";

const VOICES = [
  { id: "fish:", label: "Natural — lifelike (Fish Audio S.2)" },
  { id: "fish:47e0fe010fc1468fa2b389a3dc1db037", label: "Warm Grandmother" },
  { id: "fish:db0ba3ab933d49e786b92072a5a38c18", label: "Caring Grandmother" },
  { id: "fish:f45f7cdfe47a4a55aeb1689030b94a32", label: "Warm Storyteller" },
  { id: "fish:e686ae649ee44f219a108aacba206c1a", label: "Calm Storyteller (male)" },
  { id: "fish:3df6f0a0b0f349dbb0f9425e50c36a5b", label: "Documentary Narrator" },
  { id: "fish:792de04279ad4be0b85abc55d9eada40", label: "Deep & Warm — Benjamin" },
  { id: "fish:f6a19fe5ab494e1fa51bb1476d583a44", label: "Audiobook — Woman (US)" },
  { id: "fish:bbb58d698b5f46719fd04688dfac7359", label: "Audiobook — Man (US)" },
  { id: "openai:fable", label: "Standard voice (backup)" },
];

const FISH_VOICES = VOICES.filter((v) => v.id.startsWith("fish:"));

const MUSIC = [
  { id: "none", label: "No music" },
  { id: "ambient", label: "Ambient pad — calm" },
  { id: "warm_piano", label: "Warm & low" },
  { id: "strings", label: "Lush strings" },
  { id: "uplifting", label: "Uplifting" },
  { id: "lullaby", label: "Gentle lullaby" },
];

function yearRange(m) {
  const y = (s) => (String(s || "").match(/\d{4}/) || [""])[0];
  const b = y(m.birth_date), d = y(m.death_date);
  return b || d ? `${b || "?"}${d ? " – " + d : ""}` : "";
}

export function StoryView({ members, scopeIds, scopeName, canExport = true, onRequireLogin }) {
  const [voice, setVoice] = useState("fish:f45f7cdfe47a4a55aeb1689030b94a32");
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scriptText, setScriptText] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [musicStyle, setMusicStyle] = useState("ambient");
  const [introOutro, setIntroOutro] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [personVoice, setPersonVoice] = useState({});
  const previewRef = useRef(null);
  const [mode, setMode] = useState("all"); // all | custom
  const [picked, setPicked] = useState({}); // id -> bool
  const cache = useRef({}); // id|voice -> {script, audioPath}
  const audioRef = useRef(null);
  const idxRef = useRef(0);

  const ordered = useMemo(() => {
    if (scopeIds && scopeIds.length) {
      const byId = Object.fromEntries(members.map((m) => [m.id, m]));
      return scopeIds.map((id) => byId[id]).filter(Boolean);
    }
    return [...members].sort((a, b) => (parseInt(a.birth_date) || 9999) - (parseInt(b.birth_date) || 9999));
  }, [members, scopeIds]);

  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  const current = ordered[idx];

  const effVoice = (m) => {
    if (!m) return voice;
    const pv = personVoice[m.id];
    if (pv === "") return voice;
    if (pv) return pv;
    return m.narration_voice || voice;
  };

  const prepare = useCallback(async (i) => {
    const m = ordered[i];
    const pv = personVoice[m.id];
    const uv = pv === "" ? voice : (pv || m.narration_voice || voice);
    const key = `${m.id}|${uv}`;
    setLoading(true);
    try {
      let entry = cache.current[key];
      if (!entry) {
        const s = await api.storyScript(m.id);
        const t = await api.tts(s.script, uv);
        entry = { script: s.script, audioPath: t.path };
        cache.current[key] = entry;
      }
      setScriptText(entry.script);
      return entry;
    } finally {
      setLoading(false);
    }
  }, [ordered, voice, personVoice]);

  const clearPersonCache = (id) => {
    Object.keys(cache.current).forEach((k) => { if (k.startsWith(id + "|")) delete cache.current[k]; });
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const t = await api.tts("Hello! This is how your family's story will sound in this voice.", voice);
      const a = previewRef.current || (previewRef.current = new Audio());
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
      a.src = audioUrl(t.path);
      await a.play().catch(() => {});
    } catch { toast.error("Could not play a preview"); }
    finally { setPreviewing(false); }
  };

  const choosePersonVoice = async (v) => {
    if (!current) return;
    const val = v === "__default__" ? "" : v;
    clearPersonCache(current.id);
    setPersonVoice((p) => ({ ...p, [current.id]: val }));
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false); setStarted(false);
    try {
      await api.update(current.id, { narration_voice: val });
      toast.success(val ? "Voice set for this person" : "Using the main voice for this person");
    } catch { toast.error("Could not save voice"); }
  };

  const playIdx = useCallback(async (i) => {
    if (i < 0 || i >= ordered.length) {
      setPlaying(false);
      setStarted(false);
      return;
    }
    setIdx(i);
    idxRef.current = i;
    let entry;
    try {
      entry = await prepare(i);
    } catch {
      toast.error("Could not prepare this slide");
      return;
    }
    const audio = audioRef.current;
    audio.src = audioUrl(entry.audioPath);
    audio.play().catch(() => {});
    setPlaying(true);
    setStarted(true);
  }, [ordered.length, prepare]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => playIdx(idxRef.current + 1);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("ended", onEnded);
    };
  }, [playIdx]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else if (started) { a.play().catch(() => {}); setPlaying(true); }
    else playIdx(0);
  };

  const changeVoice = (v) => {
    setVoice(v);
    cache.current = {}; // audio must be regenerated for new voice
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
    setStarted(false);
  };

  const saveScript = async () => {
    if (!current) return;
    try {
      await api.update(current.id, { story_script: scriptText });
      clearPersonCache(current.id);
      toast.success("Narration saved");
    } catch { toast.error("Could not save"); }
  };

  const regenerate = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const s = await api.storyScript(current.id, true);
      setScriptText(s.script);
      clearPersonCache(current.id);
      toast.success("New narration written");
    } catch { toast.error("Could not regenerate"); }
    finally { setLoading(false); }
  };

  const exportMp4 = async (ids, style) => {
    setExporting(true);
    setExportMsg("Starting…");
    try {
      const { job_id, total } = await api.exportStart(ids, voice, style !== "none", style === "none" ? "ambient" : style, introOutro);
      let done = false;
      let polls = 0;
      while (!done) {
        await new Promise((r) => setTimeout(r, 4000));
        if (++polls > 90) { // ~6 min ceiling
          setExportMsg("");
          toast.error("Render is taking too long — please try again");
          break;
        }
        const st = await api.exportStatus(job_id);
        if (st.status === "rendering") setExportMsg(`Rendering… ${st.progress || 0}/${st.total || total}`);
        else if (st.status === "done") {
          done = true;
          setExportMsg("");
          const a = document.createElement("a");
          a.href = exportDownloadUrl(job_id);
          a.download = "family-story.mp4";
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast.success("Your MP4 is downloading");
        } else {
          done = true;
          setExportMsg("");
          toast.error("Render failed");
        }
      }
    } catch {
      toast.error("Could not export video");
      setExportMsg("");
    } finally {
      setExporting(false);
    }
  };

  if (!members.length) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded-3xl border border-[#D8E0E8] bg-white p-8 text-center shadow-sm">
          <Clapperboard className="mx-auto mb-2 text-[#D4A017]" size={28} />
          <p className="font-serif text-lg font-semibold text-[#082D4A]">No one to narrate yet</p>
          <p className="mt-1 text-sm text-[#64748B]">Add people to your tree, then play their story here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col p-4 sm:p-6" data-testid="story-view">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#082D4A]">Story Video</h2>
          <p className="text-sm text-[#64748B]">
            {scopeName ? <>The story of <span className="font-medium text-[#D4A017]">{scopeName}</span> &amp; descendants — {ordered.length} people.</> : <>A narrated slideshow of your family, {ordered.length} people.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={voice} onValueChange={changeVoice}>
            <SelectTrigger data-testid="voice-clone-select" className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VOICES.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={previewVoice} disabled={previewing} data-testid="preview-voice-button" variant="outline" size="icon" title="Hear this voice" className="shrink-0 border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
            {previewing ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />}
          </Button>
          <Button onClick={() => { if (!canExport) return onRequireLogin?.(); setMode("all"); setPicked(Object.fromEntries(ordered.map((m) => [m.id, true]))); setPickerOpen(true); }} disabled={exporting} data-testid="export-mp4-button" variant="outline" className="border-[#082D4A] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
            {exporting ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> {exportMsg || "Rendering…"}</> : <><Download size={16} className="mr-1.5" /> Download MP4</>}
          </Button>
        </div>
      </div>

      {/* Slide */}
      <div className="relative flex flex-1 flex-col overflow-hidden rounded-3xl border border-[#D8E0E8] bg-gradient-to-b from-[#082D4A] to-[#0b1120] p-6 text-center text-white">
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="h-40 w-40 overflow-hidden rounded-full ring-4 ring-white/20 transition-transform duration-[6000ms] ease-out"
               style={{ transform: playing ? "scale(1.08)" : "scale(1)" }}>
            {current && photoUrl(current.photo_path) ? (
              <img src={photoUrl(current.photo_path)} alt={current.full_name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-white/10 text-white/50"><User size={56} /></div>
            )}
          </div>
          <h3 className="mt-5 font-serif text-3xl font-semibold" data-testid="story-slide-name">{current?.full_name}</h3>
          {current?.nickname && <p className="text-[#D4A017]">“{current.nickname}”</p>}
          {current && yearRange(current) && (
            <p className="flex items-center justify-center gap-1.5 text-white/70">
              {yearRange(current)}
              {(isEstimatedDate(current.birth_date) || isEstimatedDate(current.death_date)) && (
                <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-medium text-white/80" data-testid="story-estimated-tag">estimated</span>
              )}
            </p>
          )}
          {current && (current.origin_place || current.current_place) && (
            <p className="mt-1 text-sm text-white/60">
              {[current.origin_place && `From ${current.origin_place}`, current.current_place && `Lives in ${current.current_place}`].filter(Boolean).join(" · ")}
            </p>
          )}
          <p className="mt-4 max-w-xl text-white/90" data-testid="story-slide-script">
            {loading ? <span className="inline-flex items-center gap-2 text-white/60"><Loader2 className="animate-spin" size={16} /> Writing the story…</span> : scriptText}
          </p>
        </div>

        <div className="mt-4 text-xs text-white/50">{idx + 1} / {ordered.length}</div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center justify-center gap-3">
        <Button variant="outline" size="icon" onClick={() => playIdx(idx - 1)} disabled={idx === 0} data-testid="story-prev-button" className="rounded-full"><SkipBack size={18} /></Button>
        <Button onClick={togglePlay} data-testid="story-play-button" className="rounded-full bg-[#D4A017] px-6 text-white hover:bg-[#B0850F]">
          {playing ? <><Pause size={18} className="mr-2" /> Pause</> : <><Play size={18} className="mr-2 fill-white" /> {started ? "Resume" : "Play Story"}</>}
        </Button>
        <Button variant="outline" size="icon" onClick={() => playIdx(idx + 1)} disabled={idx >= ordered.length - 1} data-testid="story-next-button" className="rounded-full"><SkipForward size={18} /></Button>
        <Button variant="outline" size="icon" onClick={() => playIdx(idx)} data-testid="story-replay-button" className="rounded-full"><RotateCcw size={18} /></Button>
      </div>

      {/* Script editor */}
      <div className="mt-4 rounded-2xl border border-[#D8E0E8] bg-white p-3">
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Narration for {current?.full_name}</span>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={(personVoice[current?.id] ?? current?.narration_voice) ? (personVoice[current?.id] ?? current?.narration_voice) : "__default__"} onValueChange={choosePersonVoice}>
              <SelectTrigger data-testid="person-voice-select" className="h-8 w-52 bg-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Voice: main voice</SelectItem>
                {FISH_VOICES.map((v) => <SelectItem key={v.id} value={v.id}>Voice: {v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={regenerate} disabled={loading} data-testid="story-regenerate-button"><Wand2 size={14} className="mr-1" /> Regenerate</Button>
            <Button size="sm" onClick={saveScript} data-testid="story-save-script-button" className="bg-[#082D4A] text-white hover:bg-[#08223A]"><Save size={14} className="mr-1" /> Save</Button>
          </div>
        </div>
        <Textarea value={scriptText} onChange={(e) => setScriptText(e.target.value)} rows={3} data-testid="story-script-input" placeholder="Press Play to generate a narration, then edit it here…" />
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto" data-testid="export-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#082D4A]">Create your video</DialogTitle>
            <DialogDescription>Choose who to include and whether to add gentle background music. Up to 40 people per video.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Button variant={mode === "all" ? "default" : "outline"} onClick={() => { setMode("all"); setPicked(Object.fromEntries(ordered.map((m) => [m.id, true]))); }} data-testid="export-mode-all" className={mode === "all" ? "bg-[#082D4A] text-white" : ""}>
                {scopeName ? "This branch" : "Everyone"} ({ordered.length})
              </Button>
              <Button variant={mode === "custom" ? "default" : "outline"} onClick={() => setMode("custom")} data-testid="export-mode-custom" className={mode === "custom" ? "bg-[#082D4A] text-white" : ""}>
                Choose people
              </Button>
            </div>

            {mode === "custom" && (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-[#D8E0E8] p-2">
                {ordered.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 text-sm hover:bg-[#F4F6F8]">
                    <Checkbox checked={!!picked[m.id]} onCheckedChange={(v) => setPicked((p) => ({ ...p, [m.id]: !!v }))} data-testid={`export-pick-${m.id}`} />
                    <span>{m.full_name}{m.birth_date ? <span className="text-[#64748B]"> ({String(m.birth_date).slice(0, 4)})</span> : null}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="rounded-xl bg-[#F4F6F8] p-3">
              <div className="mb-1.5 flex items-center gap-2 text-sm font-medium text-[#082D4A]"><Music2 size={15} /> Background music</div>
              <Select value={musicStyle} onValueChange={setMusicStyle}>
                <SelectTrigger data-testid="export-music-select" className="h-9 w-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MUSIC.map((mm) => <SelectItem key={mm.id} value={mm.id}>{mm.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-[#94A3B8]">Plays softly under the narration, with gentle slide fades.</p>
            </div>

            <label className="flex cursor-pointer items-center justify-between rounded-xl bg-[#F4F6F8] p-3">
              <span className="flex items-center gap-2 text-sm font-medium text-[#082D4A]"><Clapperboard size={15} /> Title &amp; credits</span>
              <Switch checked={introOutro} onCheckedChange={setIntroOutro} data-testid="export-intro-outro-switch" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPickerOpen(false)}>Cancel</Button>
            <Button
              data-testid="export-render-button"
              className="bg-[#D4A017] text-white hover:bg-[#B0850F]"
              onClick={() => {
                const ids = ordered.filter((m) => picked[m.id]).map((m) => m.id);
                if (!ids.length) return;
                setPickerOpen(false);
                exportMp4(ids, musicStyle);
              }}
            >
              <Download size={16} className="mr-1.5" /> Render video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
